/**
 * 作り直しの承認の一覧（API §4.0）— 未確定の案件に積まれた承認待ちを 1 表に集める入出力契約。
 *
 * 旧表を読んでいた横断の一覧（撤去済み）との差は 3 つである。
 *
 * | | 旧 | 作り直し |
 * |---|---|---|
 * | 案件番号 | `AIRCLOSET-139162` の文字列 | number（決定 2） |
 * | 行が指す相手 | 変更行の id（`changeId`） | その案件の変更の**ノード**（`nodeId`。決定 7） |
 * | 提案理由・判断理由 | 行が持つ | **持たない**（決定 3。理由は要求と why の層、判断は操作履歴） |
 *
 * **種別（`kind`）は `ApprovalKind` の値をそのまま使う。** 型から種別への写しは `APPROVAL_KIND_BY_NODE_TYPE` の 1 箇所が持つ。
 * 別の enum を立てると、同じ値に画面の訳語が 2 組でき、片方だけ増えたときに崩れる。
 *
 * **表示言語は入力で受け、行は 1 言語へ畳んだ本文を返す。** 3 言語の組のまま返すと、畳み方を
 * 画面が持つことになり、`?? ''` と `?? 日本語` が混ざる（本文を返す読取と同じ扱い）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの承認の一覧の入出力契約（案件を跨いだ承認待ちの行）
 * @graph-connects none
 */
import { z } from 'zod';
import { GraphSourcedStringSchema, IsoDateTimeSchema, LocaleInputShape, NonEmptyStringSchema, UntranslatedSchema, } from '../spec-graph-common.js';
import { ApprovalKindSchema } from '../spec-pj-term-approval.js';
import { NodeIdSchema, PjNumberSchema } from './pj-operations.js';
/**
 * 承認の一覧が返す行の状態 3 値。
 *
 * `open`（承認が要らない型の行）と `merged`（確定済みの案件の行）は載らない。前者はそもそも
 * 判断の対象でなく、後者は既に master へ入っていて承認を積めない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認の一覧が返す行の状態 3 値の allowlist
 * @graph-connects none
 */
export const APPROVAL_ROW_STATES = ['pending', 'approved', 'rejected'];
/** 承認の一覧が返す行の状態。 @graph-connects none */
export const ApprovalStateSchema = z.enum(APPROVAL_ROW_STATES);
/**
 * 行が表す操作 3 値。行の前後（`before` / `after`）だけから決まる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認の一覧の行が表す操作 3 値の allowlist
 * @graph-connects none
 */
export const APPROVAL_ROW_OPERATIONS = ['create', 'update', 'delete'];
/** 行が表す操作。 @graph-connects none */
export const ApprovalOperationSchema = z.enum(APPROVAL_ROW_OPERATIONS);
/**
 * 承認の一覧の 1 行。
 *
 * `label` は**材料が欠けていれば空文字**になる。復元は名前の材料（アクターのクラス等）を
 * 未設定のまま戻すので、材料が揃うまで名前を引けないノードが居る。行そのものは落とさない —
 * 落とすと、判断すべき提案が一覧から静かに消える。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認の一覧の 1 行の出力スキーマ（案件・ノード・種別・名前・操作・状態・更新日時・提案者）
 * @graph-connects none
 */
export const ApprovalRowSchema = z.object({
    pjNumber: PjNumberSchema,
    nodeId: NodeIdSchema,
    /** 承認の対象の種別（`ApprovalKind`） */
    kind: ApprovalKindSchema,
    /** 表示言語へ畳んだ名前。材料が欠けていれば空文字 */
    label: GraphSourcedStringSchema,
    /** その言語に本文が入っていないか */
    untranslated: UntranslatedSchema,
    operation: ApprovalOperationSchema,
    state: ApprovalStateSchema,
    updatedAt: IsoDateTimeSchema,
    /** 提案した人 */
    actor: NonEmptyStringSchema,
});
/**
 * `listTermChangesAcrossPjs` の入力。表示言語だけを取る。
 *
 * 案件で絞らない。この一覧を開く理由が「案件を跨いで同じ語の提案がぶつかっていないか」を
 * 見ることなので、案件で絞れる入口を契約に置くと使い方を取り違える。絞るのは画面の中である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの承認の一覧の入力スキーマ（表示言語のみ）
 * @graph-connects none
 */
export const ListTermChangesAcrossPjsInputSchema = z.object({
    ...LocaleInputShape,
});
/**
 * `listTermChangesAcrossPjs` の出力。
 *
 * `canApprove` を持たないのは、この一覧が担当者にしか返らない（`FORBIDDEN`）ためである。
 * 本人が承認を積めるかは `getApprovalAccess` が答える。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの承認の一覧の出力スキーマ
 * @graph-connects none
 */
export const ListTermChangesAcrossPjsOutputSchema = z.object({
    approvals: z.array(ApprovalRowSchema),
});
//# sourceMappingURL=approvals.js.map